import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Avatar,
    Box,
    Checkbox,
    Divider,
    Flex,
    Group,
    Image,
    Paper,
    PaperProps,
    Rating,
    Stack,
    Text,
    Tooltip,
    useMantineTheme,
} from "@mantine/core";
import { IconThumbDownFilled, IconThumbUpFilled } from "@tabler/icons-react";
import { PreviewedVideoPlayer, VideoFile } from "@testuj.to/react";
import { Review, ReviewState } from "src/graphql/generated/graphql";
import { CampaignQueryDataType } from "src/hooks/campaigns";

import { ApproveReviewModal } from "../../modals/ApproveReview";
import { FormItem } from "../FormItem";
import { LightBox } from "../LightBox";
import { Contents } from "./content";
import { Controls } from "./controls";
import { ProsAndCons } from "./prosAndCons";

export interface ReviewCardProps extends PaperProps {
    campaign: CampaignQueryDataType;
    review: Review;
    isSelected?: boolean;
    onSelect?(isSelected?: boolean);
    onReviewUpdate?();
}

export const ReviewCard = ({ campaign, review, isSelected, onSelect, onReviewUpdate, ...rest }: ReviewCardProps) => {
    const { t, i18n } = useTranslation(["common"]);
    const [selectedImage, setSelectedImage] = useState<number>(-1);
    const theme = useMantineTheme();

    const handleSelectionToggle = useCallback(() => {
        onSelect?.(!isSelected);
    }, [isSelected, onSelect]);

    return (
        <Paper {...rest} mb="md" radius="lg" withBorder>
            <Flex>
                <Box p="lg" style={{ flex: 1, minWidth: 0 }}>
                    <Flex mb="lg" align="center" style={{ width: "100%", minWidth: 0 }}>
                        {typeof onSelect === "function" && (
                            <Checkbox mr="md" checked={isSelected} onChange={handleSelectionToggle} />
                        )}
                        <Group spacing="md" style={{ flex: 1, minWidth: 0 }} noWrap>
                            {review?.reviewer.profileImage ? (
                                <Avatar src={`${review?.reviewer.profileImage.src}`} size="lg" />
                            ) : (
                                <Avatar size="lg">
                                    {String(
                                        `${review?.reviewer.profile.firstName} ${review?.reviewer.profile.lastName}`
                                    )
                                        .toUpperCase()
                                        .split(" ")
                                        .map((item) => item[0])
                                        .join("")}
                                </Avatar>
                            )}
                            <Stack spacing="0" style={{ flex: 1, minWidth: 0 }}>
                                <Text
                                    weight={500}
                                    style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                                >
                                    <Tooltip position="top-start" label={t("username")} withArrow>
                                        <span>
                                            {review?.reviewer.profile.firstName} {review?.reviewer.profile.lastName}
                                        </span>
                                    </Tooltip>
                                </Text>
                                <Text
                                    color="dimmed"
                                    style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                                >
                                    <Tooltip position="bottom-start" label={t("date")} withArrow>
                                        <span>{}</span>
                                    </Tooltip>
                                </Text>
                            </Stack>
                        </Group>
                        <Flex align="start">
                            <Box
                                ml="md"
                                px="xs"
                                py="2px"
                                bg="gray.1"
                                style={{
                                    borderRadius: "999px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <Flex display="inline-flex" align="center">
                                    <Text mr="xs">
                                        {(Number(review?.totalRating) / 2).toLocaleString(i18n?.language)} / 5
                                    </Text>
                                    <Rating color="orange" value={review?.totalRating / 2} fractions={2} readOnly />
                                </Flex>
                            </Box>
                            <Box
                                ml="md"
                                px="xs"
                                py="2px"
                                bg={review?.wouldRecommend ? "green.1" : "red.1"}
                                style={{
                                    borderRadius: "999px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <Flex display="inline-flex" align="center">
                                    <Box
                                        mr="6px"
                                        h="18px"
                                        style={{
                                            color: review?.wouldRecommend ? theme.colors.green[9] : theme.colors.red[9],
                                        }}
                                    >
                                        {review?.wouldRecommend ? (
                                            <IconThumbUpFilled size="18px" />
                                        ) : (
                                            <IconThumbDownFilled size="18px" />
                                        )}
                                    </Box>
                                    <Text color={review?.wouldRecommend ? "green.9" : "red.9"}>
                                        {review?.reviewer?.profile?.firstName}{" "}
                                        {(review?.wouldRecommend
                                            ? t("recommendsat")
                                            : t("doesntrecommendat")
                                        ).toLowerCase()}{" "}
                                        {review?.wouldRecommendPercent}%
                                    </Text>
                                </Flex>
                            </Box>
                        </Flex>
                    </Flex>
                    <ProsAndCons mb="lg" pros={review?.pros} cons={review?.cons} />
                    <Contents contents={review?.contents} />
                    {(!!review?.images?.length || !!review?.videos?.length) && (
                        <Group mt="md">
                            {(review?.images || []).map((image, index) => (
                                <Image
                                    key={`${index}${image?.id}`}
                                    src={`${image?.src}?h=120`}
                                    radius="sm"
                                    width="auto"
                                    fit="contain"
                                    height={120}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setSelectedImage(index)}
                                />
                            ))}
                            {(review?.videos || []).map((video, index) =>
                                video?.isBeingProcessed ? (
                                    <Text key={`${index}${video?.id}`} color="dimmed">
                                        {t("review_video_processing")}
                                    </Text>
                                ) : (
                                    <Box key={`${index}${video?.id}`} display="inline-block">
                                        <PreviewedVideoPlayer
                                            video={video as VideoFile}
                                            height={120}
                                            controlSize={16}
                                            progressBarPreviewHeight={50}
                                        />
                                    </Box>
                                )
                            )}
                        </Group>
                    )}
                    {selectedImage >= 0 && (
                        <LightBox
                            images={review?.images}
                            selectedIndex={selectedImage}
                            onClose={() => setSelectedImage(-1)}
                        />
                    )}
                    {review?.rejectionMessage &&
                        (review?.state === ReviewState.Submitted || review?.state === ReviewState.Rejected) && (
                            <>
                                <Divider my="md" color="gray.2" />
                                <FormItem label={t("review_rejectionmessage")}>{review?.rejectionMessage}</FormItem>
                            </>
                        )}
                    <Divider my="md" color="gray.2" />
                </Box>
                <Divider orientation="vertical" color="gray.2" />
                <Box w="142px" p="lg">
                    <Controls
                        reviewState={review?.state}
                        onApproveClick={() =>
                            ApproveReviewModal.display({
                                review,
                                campaign,
                                onApprove: onReviewUpdate,
                            })
                        }
                    />
                </Box>
            </Flex>
        </Paper>
    );
};
